import {
  ActionIcon,
  Button,
  Flex,
  Group,
  Popover,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from "@mantine/core";
import {
  IconCheck,
  IconEdit,
  IconEye,
  IconFileDescription,
  IconX,
} from "@tabler/icons-react";
import React, { useEffect, useRef, useState } from "react";

interface PromotionDescriptionPopoverProps {
  value?: string;
  onChange: (value: string) => void;
  amountDiscountTotal?: number;
  hasVoucher?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  opened?: boolean;
  onOpenedChange?: (opened: boolean) => void;
}

export const PromotionDescriptionPopover: React.FC<
  PromotionDescriptionPopoverProps
> = ({
  value = "",
  onChange,
  disabled = false,
  readOnly = false,
  opened: externalOpened,
  onOpenedChange,
}) => {
  const [internalOpened, setInternalOpened] = useState(false);
  const isControlled = externalOpened !== undefined;
  const opened = isControlled ? externalOpened : internalOpened;

  const setOpened = (nextOpened: boolean) => {
    if (!isControlled) {
      setInternalOpened(nextOpened);
    }
    onOpenedChange?.(nextOpened);
  };

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [tempText, setTempText] = useState(value);

  // Đồng bộ giá trị khi prop value thay đổi
  useEffect(() => {
    setTempText(value || "");
  }, [value]);

  // Focus vào textarea khi popover mở (nếu không phải readOnly)
  useEffect(() => {
    if (opened && !readOnly) {
      setTimeout(() => {
        textareaRef.current?.focus();
        textareaRef.current?.select();
      }, 50);
    }
  }, [opened, readOnly]);

  const hasValue = !!value?.trim();
  const isActionDisabled = disabled && !readOnly;

  const handleSave = () => {
    if (readOnly) return;
    onChange(tempText.trim());
    setOpened(false);
  };

  const handleClear = () => {
    if (readOnly) return;
    setTempText("");
    onChange("");
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      withArrow
      shadow="md"
      radius="md"
      width={320}
      withinPortal
      disabled={isActionDisabled}
    >
      <Popover.Target>
        <div style={{ pointerEvents: "auto", display: "inline-flex" }}>
          <Tooltip
            label={
              hasValue
                ? `Diễn giải KM: ${value} (${readOnly ? "Click để xem" : "Click để sửa"})`
                : readOnly
                ? "Không có diễn giải khuyến mãi"
                : "Bấm vào đây để nhập diễn giải CTKM (Bắt buộc để nhập tiền KM)"
            }
            position="top"
            withArrow
            multiline
            w={260}
          >
            <ActionIcon
              size="sm"
              variant={hasValue ? "filled" : "light"}
              color={hasValue ? "teal" : readOnly ? "gray" : "blue"}
              disabled={isActionDisabled}
              onClick={() => {
                if (!isActionDisabled) {
                  setOpened(!opened);
                }
              }}
              style={{
                cursor: isActionDisabled ? "not-allowed" : "pointer",
                transition: "all 0.2s ease",
                flexShrink: 0,
                pointerEvents: "auto",
              }}
              aria-label="Diễn giải khuyến mãi"
            >
              {hasValue ? (
                <IconFileDescription size={15} />
              ) : readOnly ? (
                <IconEye size={15} />
              ) : (
                <IconEdit size={15} />
              )}
            </ActionIcon>
          </Tooltip>
        </div>
      </Popover.Target>

      <Popover.Dropdown p="xs" style={{ pointerEvents: "auto" }}>
        <Stack gap={8}>
          <Flex justify="space-between" align="center">
            <Group gap={6}>
              <IconFileDescription size={15} color="#228be6" />
              <Text size="xs" fw={600} c="dark">
                {readOnly ? "Thông tin diễn giải KM" : "Diễn giải KM tổng đơn"}
              </Text>
            </Group>
            {!readOnly && tempText && (
              <Button
                variant="subtle"
                color="red"
                size="compact-xs"
                leftSection={<IconX size={12} />}
                onClick={handleClear}
                styles={{ root: { height: 20, padding: "0 4px" } }}
              >
                Xóa
              </Button>
            )}
          </Flex>

          <Textarea
            ref={textareaRef}
            placeholder={
              readOnly
                ? "Không có thông tin diễn giải"
                : "Nhập lý do, căn cứ hoặc mã CTKM tổng đơn..."
            }
            readOnly={readOnly}
            variant={readOnly ? "filled" : "default"}
            size="xs"
            minRows={2}
            maxRows={5}
            autosize
            value={readOnly ? value || "Không có diễn giải khuyến mãi" : tempText}
            onChange={(e) => !readOnly && setTempText(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (!readOnly && e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSave();
              }
            }}
            styles={{
              input: {
                fontSize: "12px",
                backgroundColor: readOnly ? "#f8f9fa" : undefined,
                cursor: readOnly ? "default" : undefined,
                color: readOnly && !hasValue ? "#868e96" : undefined,
              },
            }}
          />

          <Flex
            justify={readOnly ? "flex-end" : "space-between"}
            align="center"
          >
            {!readOnly && (
              <Text size="10px" c="dimmed">
                Nhấn Enter để lưu
              </Text>
            )}
            <Group gap={6}>
              <Button
                size="xs"
                variant={readOnly ? "filled" : "default"}
                color={readOnly ? "blue" : undefined}
                onClick={() => setOpened(false)}
                styles={{ root: { height: 24, padding: "0 12px" } }}
              >
                Đóng
              </Button>
              {!readOnly && (
                <Button
                  size="xs"
                  variant="filled"
                  color="blue"
                  leftSection={<IconCheck size={12} />}
                  onClick={handleSave}
                  styles={{ root: { height: 24, padding: "0 8px" } }}
                >
                  Lưu
                </Button>
              )}
            </Group>
          </Flex>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};

export default PromotionDescriptionPopover;
